// Change Password
// ----------------
// Dark-themed page shown after login when Cognito requires a password change.
// Matches the visual style of Login, ForgotPassword, and ResetPassword.

import React, { useState, useCallback } from "react";
import { Loader2, Lock } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { FormField } from "@/components/ui/form-field";
import { EMPTY_STRING, NIMIS_WEBSITE_URL } from "@/constants/definitions";
import CognitoLoginClass from "@/models/cognitoLogin";
import { loadDashboard } from "@/utils/navigation";
import PasswordValidationClass from "@/utils/passwordValidation";

interface ChangePasswordProps {
  getUserDetails: () => Promise<void>;
  cognitologin: CognitoLoginClass;
  navigate: (path: string) => void;
}

export default function ChangePassword({
  getUserDetails,
  cognitologin,
  navigate,
}: ChangePasswordProps) {
  const [oldPassword, setOldPassword] = useState("");
  const [password1, setPassword1] = useState("");
  const [password2, setPassword2] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async () => {
    const validate = new PasswordValidationClass();
    let validationError = validate.validateFirstPassword(password1, "", true);
    if (!validationError) {
      validationError = validate.validateSecondPassword(password1, password2, "");
    }

    if (validationError) {
      setErrorMessage(validationError);
      return;
    }

    try {
      setIsLoading(true);
      setErrorMessage("");

      const result = (await cognitologin.changePassword(oldPassword, password1)) || "";
      if (result === EMPTY_STRING) {
        await getUserDetails();
        loadDashboard(navigate);
      } else {
        setErrorMessage(result);
      }
    } catch (changeError: any) {
      setErrorMessage(changeError?.message || String(changeError));
    } finally {
      setIsLoading(false);
    }
  }, [oldPassword, password1, password2, cognitologin, getUserDetails, navigate]);

  const isFormValid = oldPassword && password1 && password2 && password1 === password2;

  return (
    <div data-theme="dark" className="relative" style={{ background: "hsla(222, 70%, 8%, 0.85)", minHeight: "calc(100vh + 1px)" }}>
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 60% 50% at 20% 30%, rgba(59, 130, 246, 0.08) 0%, transparent 70%),
            radial-gradient(ellipse 50% 40% at 80% 70%, rgba(14, 165, 233, 0.05) 0%, transparent 70%)
          `,
        }}
      />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl items-center px-4 sm:px-6 pt-6 pb-6">
        <a href={NIMIS_WEBSITE_URL}>
          <img src="/nimis-intelligence-white-logo.png" alt="NIMIS Intelligence" className="h-12 w-auto" />
        </a>
      </div>

      <div className="relative z-10 flex justify-center pt-[12vh]">
        <div className="w-full max-w-sm mx-4">
          <div className="rounded-xl bg-white/[0.03] backdrop-blur-sm border border-white/[0.08] p-8">
            <div className="text-center mb-6">
              <h1 className="text-xl font-semibold text-white">Update your passphrase</h1>
              <p className="text-sm text-white/40 mt-1">Your passphrase needs to be changed before continuing.</p>
            </div>

            {errorMessage && (
              <div className="mb-4 rounded-lg bg-red-500/10 border border-red-500/20 px-4 py-3 text-sm text-red-300">
                {errorMessage}
              </div>
            )}

            <div className="space-y-4">
              <FormField label="Current Passphrase" htmlFor="oldPassword" variant="dark">
                <Input
                  id="oldPassword"
                  type="password"
                  variant="dark"
                  placeholder="Current passphrase"
                  value={oldPassword}
                  onChange={(changeEvent) => { setOldPassword(changeEvent.target.value); setErrorMessage(""); }}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                />
              </FormField>

              <FormField label="New Passphrase" htmlFor="password1" variant="dark">
                <Input
                  id="password1"
                  type="password"
                  variant="dark"
                  placeholder="New passphrase"
                  value={password1}
                  onChange={(changeEvent) => { setPassword1(changeEvent.target.value); setErrorMessage(""); }}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                />
              </FormField>

              <FormField label="Confirm New Passphrase" htmlFor="password2" variant="dark">
                <Input
                  id="password2"
                  type="password"
                  variant="dark"
                  placeholder="Confirm new passphrase"
                  value={password2}
                  onChange={(changeEvent) => { setPassword2(changeEvent.target.value); setErrorMessage(""); }}
                  disabled={isLoading}
                  leftIcon={<Lock className="h-4 w-4" />}
                />
              </FormField>

              <Button
                onClick={handleSubmit}
                disabled={isLoading || !isFormValid}
                className="w-full h-10 bg-blue-600 hover:bg-blue-500 text-white font-medium rounded-lg transition-colors"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Updating...
                  </>
                ) : (
                  "Update passphrase"
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
